import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Badge, Button, Card, Col, Container, Modal, Row, Spinner } from "react-bootstrap";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { getPublicTracking } from "../services/tripTrackingApi";
import { TripPolicyAcceptedNote, TripPolicyLockedView } from "../components/TripPolicyAcceptance";
import { formatDateDMY, formatDateTimeDMY12 } from "../utils/dateFormat";
import TrackingEmergencyContacts from "./TrackingEmergencyContacts";
import TrackingIdCards from "./TrackingIdCards";
import { listCustomerIdCards } from "../services/customerIdCardApi";
import TrackingPageTour, { POLICY_PAGE_TOUR_STEPS, TRIP_PAGE_TOUR_STEPS } from "./TrackingPageTour";
import TrackingLiveTrip from "./TrackingLiveTrip";
import TrackingPickupModal from "./TrackingPickupModal";
import { buildTripMapModel, canChangePickup } from "../components/tripMap/tripMapModel";

// The map and "here now" panel catch up on their own while the page is open and on screen.
const LIVE_REFRESH_MS = 60 * 1000;

// Remembered per trip link and per stage on this phone: the guide opens once on its own before the
// policy is accepted, and once more after, when the full trip details appear.
const tourSeenKey = (token, stage) => `io-trip-page-guide:${token}:${stage}`;
function tourSeen(token, stage) {
  try {
    return window.localStorage.getItem(tourSeenKey(token, stage)) === "done";
  } catch (error) {
    return false;
  }
}
function markTourSeen(token, stage) {
  try {
    window.localStorage.setItem(tourSeenKey(token, stage), "done");
  } catch (error) {
    // Private browsing: the guide just opens again next time.
  }
}

export default function PublicTrackingPage() {
  const { token } = useParams();
  const [tracking, setTracking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [now, setNow] = useState(() => new Date());
  const [emergencyShown, setEmergencyShown] = useState(false);

  // Only the first load blanks the page; a refresh keeps what is on screen until the answer comes.
  const load = useCallback(async ({ silent = false } = {}) => {
    if (silent) setRefreshing(true);
    else setLoading(true);
    try {
      setTracking(await getPublicTracking(token));
      setNow(new Date());
    } catch (error) {
      if (!silent) {
        toast.error(error?.response?.data?.message || "Tracking link not found");
        setTracking(null);
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const liveView = Boolean(tracking && !tracking.policy?.locked);
  const tripOver = tracking?.tripStatus === "COMPLETED" || Boolean(tracking?.tripEnded);
  useEffect(() => {
    if (!liveView || tripOver) return undefined;
    const timer = setInterval(() => {
      if (document.visibilityState === "visible") load({ silent: true });
      else setNow(new Date());
    }, LIVE_REFRESH_MS);
    return () => clearInterval(timer);
  }, [liveView, tripOver, load]);

  const mapModel = useMemo(() => (liveView ? buildTripMapModel(tracking, now) : null), [liveView, tracking, now]);
  const hotelGroups = useMemo(() => groupHotelsByDay(tracking?.dayHotels || []), [tracking]);
  const hotelDayGroups = useMemo(() => Array.from(hotelGroups.entries()).sort(([left], [right]) => left - right), [hotelGroups]);

  // The guide explains whichever page the customer has: the policy screen while it is locked, the
  // full trip page once accepted.
  const [tourOpen, setTourOpen] = useState(false);
  const [tourClosedThisVisit, setTourClosedThisVisit] = useState(false);
  const tourStage = tracking ? (tracking.policy?.locked ? "policy" : "trip") : null;
  useEffect(() => {
    if (!tourStage || tourSeen(token, tourStage)) return undefined;
    // A moment for the emergency card to load, so it is part of the guide.
    const timer = setTimeout(() => setTourOpen(true), 1200);
    return () => clearTimeout(timer);
  }, [tourStage, token]);
  const closeTour = useCallback(() => {
    setTourOpen(false);
    setTourClosedThisVisit(true);
    if (tourStage) markTourSeen(token, tourStage);
  }, [token, tourStage]);

  // The customer's ID cards, once the policy is accepted. Null until loaded, so the reminder below
  // never fires on a list it has not seen.
  const [idCards, setIdCards] = useState(null);
  const idInputRef = useRef(null);
  useEffect(() => {
    if (tourStage !== "trip") return undefined;
    let cancelled = false;
    listCustomerIdCards(token)
      .then((cards) => { if (!cancelled) setIdCards(Array.isArray(cards) ? cards : []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [tourStage, token]);
  // Every visit with nothing uploaded gets the reminder, once the page guide is out of the way.
  const [idReminderClosed, setIdReminderClosed] = useState(false);
  const showIdReminder = tourStage === "trip" && Array.isArray(idCards) && idCards.length === 0
    && !idReminderClosed && !tourOpen && !loading && (tourClosedThisVisit || tourSeen(token, "trip"));
  // The pickup point: set from the Pickup row of Day 1, with a reminder every visit until it is set.
  // It waits its turn behind the ID card reminder, and skips the visit the file picker was opened.
  const [pickupOpen, setPickupOpen] = useState(false);
  const [pickupReminderClosed, setPickupReminderClosed] = useState(false);
  const [idUploadStarted, setIdUploadStarted] = useState(false);
  const showPickupReminder = liveView && !tracking.pickupPoint && canChangePickup(tracking)
    && !pickupReminderClosed && !pickupOpen && !tourOpen && !loading && (tourClosedThisVisit || tourSeen(token, "trip"))
    && Array.isArray(idCards) && !showIdReminder && !idUploadStarted;
  const openPickupFromReminder = () => {
    setPickupReminderClosed(true);
    setPickupOpen(true);
  };

  const uploadFromReminder = () => {
    setIdReminderClosed(true);
    setIdUploadStarted(true);
    // Straight from the tap, so the browser lets the file picker open.
    idInputRef.current?.click();
    document.querySelector('[data-tour="id-cards"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  return (
    <div className="public-tracking-page">
      {!loading && liveView ? (
        <TrackingLiveTrip
          tracking={tracking}
          model={mapModel}
          now={now}
          emergencyShown={emergencyShown}
          refreshing={refreshing}
          onRefresh={() => load({ silent: true })}
          onCopyLink={() => copyLink(window.location.href)}
          onGuide={() => setTourOpen(true)}
          onEditPickup={() => setPickupOpen(true)}
        >
          <div className="public-tracking-content">
            <TrackingEmergencyContacts token={token} onShownChange={setEmergencyShown} />
            <TripPolicyAcceptedNote policy={tracking.policy} />
            {Array.isArray(idCards) ? (
              <div className="mt-3">
                <TrackingIdCards token={token} cards={idCards} onChange={setIdCards} inputRef={idInputRef} />
              </div>
            ) : null}
            <div data-tour="payment"><CustomerPaymentSchedule schedule={tracking.paymentSchedule} /></div>
            <div data-tour="trip-info"><CustomerTripInfo tracking={tracking} /></div>
            {hotelDayGroups.length ? <div data-tour="hotels"><CustomerHotelsSection groups={hotelDayGroups} /></div> : null}
          </div>
        </TrackingLiveTrip>
      ) : (
      <Container className="py-3 py-md-4">
        {loading ? (
          <Card className="public-tracking-loading shadow-sm">
            <Card.Body className="py-5 text-center"><Spinner /></Card.Body>
          </Card>
        ) : tracking ? (
          <div className="public-tracking-shell">
            <div className="public-tracking-hero">
              <div className="public-tracking-hero-main">
                <div className="public-tracking-brand">
                  <img src="/img/icon.png" alt="Indian Odyssey" />
                  <div>
                    <div className="public-tracking-brand-name">Indian Odyssey</div>
                    <div className="public-tracking-brand-subtitle">Trip Tracking Timeline</div>
                  </div>
                </div>
                <div className="public-tracking-trip-title">
                  <h1>{tracking.tripTitle || `Booking #${tracking.bookingId}`}</h1>
                  <Badge bg={statusVariant(tracking.tripStatus)}>{tracking.tripStatus || "PENDING"}</Badge>
                </div>
                <div className="public-tracking-trip-meta">
                  <span>Travel {formatDateDMY(tracking.vehicleStartDate || tracking.travelDate, "-")}</span>
                  <span>{tracking.duration || "-"}</span>
                  <span>{tracking.destination || "-"}</span>
                </div>
              </div>
              <div className="public-tracking-actions" data-tour="page-actions">
                <Button size="sm" variant="light" onClick={() => load({ silent: true })} disabled={refreshing}>Refresh</Button>
                <Button size="sm" variant="outline-light" onClick={() => copyLink(window.location.href)}>Copy Link</Button>
                <Button size="sm" variant="outline-light" onClick={() => setTourOpen(true)}>Page Guide</Button>
              </div>
            </div>

            {/* Once the policy is accepted the page becomes the live map view above. */}
            <div className="public-tracking-content">
              <div data-tour="payment"><CustomerPaymentSchedule schedule={tracking.paymentSchedule} /></div>
              <TripPolicyLockedView tracking={tracking} token={token} onAccepted={setTracking} />
            </div>
          </div>
        ) : (
          <Card body className="public-tracking-empty text-center text-muted py-5">Tracking link not found.</Card>
        )}
      </Container>
      )}
      <TrackingPageTour
        open={tourOpen && Boolean(tourStage) && !loading}
        steps={tourStage === "policy" ? POLICY_PAGE_TOUR_STEPS : TRIP_PAGE_TOUR_STEPS}
        onClose={closeTour}
      />
      <Modal show={showIdReminder} onHide={() => setIdReminderClosed(true)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="h5">
            <i className="bi bi-person-vcard me-2" aria-hidden="true" />Upload your ID card
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Please upload your Aadhaar card or another government photo ID before your trip. It only takes a minute.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setIdReminderClosed(true)}>Later</Button>
          <Button variant="primary" onClick={uploadFromReminder}>Upload Now</Button>
        </Modal.Footer>
      </Modal>
      <Modal show={showPickupReminder} onHide={() => setPickupReminderClosed(true)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="h5">
            <i className="bi bi-geo-alt-fill me-2" aria-hidden="true" style={{ color: "#047857" }} />Set your pickup point
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Choose the exact spot and time for your pickup, so your driver comes straight to you. It only takes a minute.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setPickupReminderClosed(true)}>Later</Button>
          <Button variant="success" onClick={openPickupFromReminder}>Set Pickup</Button>
        </Modal.Footer>
      </Modal>
      {liveView ? (
        <TrackingPickupModal
          show={pickupOpen}
          token={token}
          tracking={tracking}
          onHide={() => setPickupOpen(false)}
          onSaved={() => {
            setPickupOpen(false);
            load({ silent: true });
          }}
        />
      ) : null}
    </div>
  );
}

// What the customer pays and when: 25% right after boarding, the rest on Day 1 after hotel
// check-in. Only on bookings made on these terms; older ones have no schedule.
function CustomerPaymentSchedule({ schedule }) {
  if (!schedule) return null;
  const money = (value) => `₹${Number(value || 0).toLocaleString("en-IN")}`;
  const stage = (label, amount, due) => (
    <div className="d-flex flex-wrap justify-content-between gap-2 py-1 border-top">
      <span>{label}</span>
      <span className="fw-semibold">
        {money(amount)}{" "}
        {due <= 0 ? (
          <Badge bg="success">Paid</Badge>
        ) : due < amount ? (
          <Badge bg="warning" text="dark">{money(due)} due</Badge>
        ) : (
          <Badge bg="danger">Due</Badge>
        )}
      </span>
    </div>
  );
  return (
    <Card body className="public-tracking-info-card">
      <div className="public-tracking-info-icon">₹</div>
      <div className="fw-semibold mb-2">Payment Schedule</div>
      <div className="small">
        <div className="d-flex flex-wrap justify-content-between gap-2 py-1">
          <span>Total package amount</span>
          <span className="fw-semibold">{money(schedule.totalAmount)}</span>
        </div>
        <div className="d-flex flex-wrap justify-content-between gap-2 py-1 border-top">
          <span>Paid so far</span>
          <span className="fw-semibold">{money(schedule.paidAmount)}</span>
        </div>
        {stage("Pay right after boarding the vehicle (25%)", schedule.afterBoardingAmount, schedule.afterBoardingDue)}
        {stage("Pay on Day 1 immediately after hotel check-in", schedule.dayOneAmount, schedule.dayOneDue)}
        <div className="d-flex flex-wrap justify-content-between gap-2 py-1 border-top">
          <span className="fw-semibold">Balance remaining</span>
          <span className={`fw-bold ${schedule.balanceDue > 0 ? "text-danger" : "text-success"}`}>{money(schedule.balanceDue)}</span>
        </div>
      </div>
    </Card>
  );
}

function CustomerTripInfo({ tracking }) {
  const vehicle = tracking.vehicle;
  const hasDriver = tracking.driverName || tracking.driverPhone;
  const hasVehicle = vehicle?.name || vehicle?.registrationNumber || vehicle?.category || vehicle?.seater || vehicle?.ac != null;
  const refreshmentRoom = tracking.refreshmentRoom;
  const hasVehicleTiming = tracking.vehiclePickupDateTime || tracking.vehicleDropDateTime;
  if (!hasDriver && !hasVehicle && !refreshmentRoom && !hasVehicleTiming) return null;
  return (
    <Row className="g-3 public-tracking-info-grid">
      {hasVehicleTiming ? (
        <Col xs={12}>
          <Card body className="public-tracking-info-card">
            <div className="public-tracking-info-icon">PD</div>
            <div className="fw-semibold mb-2">Vehicle Pickup & Drop</div>
            <Row className="g-2 small">
              <Col md={6}>
                <span className="text-muted">Vehicle Pickup Date & Time:</span>{" "}
                {formatDateTimeDMY12(tracking.vehiclePickupDateTime, "-")}
              </Col>
              <Col md={6}>
                <span className="text-muted">Vehicle Drop Date & Time:</span>{" "}
                {formatDateTimeDMY12(tracking.vehicleDropDateTime, "-")}
              </Col>
            </Row>
          </Card>
        </Col>
      ) : null}
      <Col md={6}>
        <Card body className="public-tracking-info-card h-100">
          <div className="public-tracking-info-icon">DR</div>
          <div className="fw-semibold mb-2">Driver Details</div>
          {hasDriver ? (
            <div className="small">
              <div><span className="text-muted">Driver:</span> {tracking.driverName || "-"}</div>
              <div><span className="text-muted">Phone:</span> {tracking.driverPhone || "-"}</div>
            </div>
          ) : (
            <div className="small text-muted">Driver details will appear after assignment.</div>
          )}
        </Card>
      </Col>
      <Col md={6}>
        <Card body className="public-tracking-info-card h-100">
          <div className="public-tracking-info-icon public-tracking-info-icon-alt">VH</div>
          <div className="fw-semibold mb-2">Vehicle Details</div>
          {hasVehicle ? (
            <div className="small">
              <div><span className="text-muted">Vehicle:</span> {vehicle.name || "-"}</div>
              <div><span className="text-muted">Registration:</span> {vehicle.registrationNumber || "-"}</div>
              <div><span className="text-muted">Category:</span> {vehicle.category || "-"}</div>
              <div><span className="text-muted">Seater:</span> {vehicle.seater || "-"}</div>
              <div><span className="text-muted">AC:</span> {vehicle.ac ? "AC" : "Non AC"}</div>
            </div>
          ) : (
            <div className="small text-muted">Vehicle details will appear after assignment.</div>
          )}
        </Card>
      </Col>
      {refreshmentRoom ? (
        <Col md={6}>
          <Card body className="public-tracking-info-card h-100">
            <div className="public-tracking-info-icon public-tracking-info-icon-alt">RF</div>
            <div className="fw-semibold mb-2">Refreshment Room</div>
            <div className="small">
              <div><span className="text-muted">Name:</span> {refreshmentRoom.name || "Refreshments"}</div>
              {refreshmentRoom.city ? <div><span className="text-muted">City:</span> {refreshmentRoom.city}</div> : null}
              {refreshmentRoom.description ? <div className="mt-1">{refreshmentRoom.description}</div> : null}
              {refreshmentRoom.googleMapLink ? (
                <Button
                  as="a"
                  href={refreshmentRoom.googleMapLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  size="sm"
                  className="public-tracking-map-link mt-2"
                >
                  Open Map
                </Button>
              ) : null}
            </div>
          </Card>
        </Col>
      ) : null}
    </Row>
  );
}

function CustomerHotelsSection({ groups }) {
  return (
    <div className="public-tracking-section public-tracking-hotels">
      <div className="public-tracking-section-title">
        <div>
          <h2>Day Wise Stay</h2>
        </div>
      </div>
      <div className="public-tracking-hotel-list">
        {groups.map(([dayNumber, hotels]) => (
          <div className="public-tracking-hotel-day" key={dayNumber}>
            <div className="public-tracking-hotel-day-label">
              <span>{hotels[0]?.dayLabel || `Day ${dayNumber}`}</span>
              {hotels[0]?.stayDate ? (
                <span className="public-tracking-hotel-day-date">{formatDateDMY(hotels[0].stayDate, "")}</span>
              ) : null}
            </div>
            <div className="public-tracking-hotel-items">
              {hotels.map((hotel) => (
                <CustomerHotelRow hotel={hotel} key={`${hotel.dayNumber}-${hotel.hotelId || hotel.hotelCode}`} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CustomerHotelRow({ hotel }) {
  const mapLink = String(hotel.googleMapLink || "").trim();
  const facilities = Array.isArray(hotel.includedFacilities) ? hotel.includedFacilities.filter(Boolean) : [];
  const caretakerName = String(hotel.caretakerName || "").trim();
  const caretakerNumber = String(hotel.caretakerNumber || "").trim();
  return (
    <div className="public-tracking-hotel-row">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
        <div>
          <div className="fw-semibold">{hotel.hotelName || "Stay"}</div>
          <div className="small text-muted">{hotel.hotelCode || "-"}</div>
          {caretakerName || caretakerNumber ? (
            <div className="small mt-1">
              <span className="text-muted">Caretaker:</span>{" "}
              {caretakerName}
              {caretakerName && caretakerNumber ? " - " : ""}
              {caretakerNumber ? <a href={`tel:${caretakerNumber.replace(/[^\d+]/g, "")}`}>{caretakerNumber}</a> : null}
            </div>
          ) : null}
          {facilities.length ? (
            <div className="small mt-1">
              <span className="text-muted">Included facilities:</span>{" "}
              {facilities.join(", ")}
            </div>
          ) : null}
        </div>
        {mapLink ? (
          <Button
            as="a"
            href={mapLink}
            target="_blank"
            rel="noopener noreferrer"
            size="sm"
            className="public-tracking-map-link"
          >
            Open Map
          </Button>
        ) : (
          <Badge bg="secondary">Map unavailable</Badge>
        )}
      </div>
    </div>
  );
}

function groupHotelsByDay(hotels) {
  const groups = new Map();
  (hotels || []).forEach((hotel) => {
    const dayNumber = Number(hotel.dayNumber || 1);
    if (!groups.has(dayNumber)) groups.set(dayNumber, []);
    groups.get(dayNumber).push(hotel);
  });
  return groups;
}

function statusVariant(status) {
  if (status === "COMPLETED") return "success";
  if (status === "ONGOING") return "warning";
  if (status === "UPCOMING") return "primary";
  return "secondary";
}

function copyLink(link) {
  navigator.clipboard?.writeText(link);
  toast.success("Tracking link copied");
}
